import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { toast } from 'sonner';
import { z } from 'zod';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';

const emailSchema = z.email();

export interface ParsedEmailRecipient {
  email: string;
  valid: boolean;
}

export const parseEmailRecipients = (value: string): ParsedEmailRecipient[] => {
  const seen = new Set<string>();
  return value
    .split(/[,;\s]+/)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0 && !seen.has(email) && seen.add(email))
    .map((email) => ({ email, valid: emailSchema.safeParse(email).success }));
};

export interface RecipientPresentation {
  disabled?: boolean;
  emailSuffix?: ReactNode;
  badge?: ReactNode;
}

interface AddUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description: ReactNode;
  onSubmit: (emails: string[]) => Promise<{ failedEmails?: string[] }>;
  getRecipientPresentation?: (email: string) => RecipientPresentation;
  defaultEmail?: string;
  maxRecipients?: number;
  inputButtonLabel?: string;
  submitButtonLabel: (count: number) => ReactNode;
  submitErrorMessage?: string | ((error: unknown) => string);
}

export function AddUsersDialog({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  getRecipientPresentation = () => ({}),
  defaultEmail = '',
  maxRecipients = 100,
  inputButtonLabel = 'Add users',
  submitButtonLabel,
  submitErrorMessage = 'Users could not be added. Please try again.',
}: AddUsersDialogProps) {
  const [input, setInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recipientsScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setInput(defaultEmail.trim());
      setRecipients([]);
    }
  }, [defaultEmail, open]);

  const pendingRecipients = useMemo(() => parseEmailRecipients(input), [input]);
  const recipientPresentations = recipients.map((email) => ({ email, ...getRecipientPresentation(email) }));
  const submittableRecipients = recipientPresentations.filter(({ disabled }) => !disabled);

  const stageInput = () => {
    if (pendingRecipients.length === 0) return;

    const invalidCount = pendingRecipients.filter(({ valid }) => !valid).length;
    const currentEmails = new Set(recipients);
    const nextRecipients = [
      ...recipients,
      ...pendingRecipients.filter(({ email, valid }) => valid && !currentEmails.has(email)).map(({ email }) => email),
    ];

    if (invalidCount > 0) {
      toast.error(
        `${invalidCount} invalid ${invalidCount === 1 ? 'email address was' : 'email addresses were'} not added.`,
      );
    }

    setRecipients(nextRecipients);
    setInput('');
    requestAnimationFrame(() => {
      recipientsScrollRef.current?.scrollTo({ top: recipientsScrollRef.current.scrollHeight, behavior: 'smooth' });
    });
  };

  const handleSubmit = async () => {
    if (submittableRecipients.length === 0) return;
    if (submittableRecipients.length > maxRecipients) {
      toast.error(`You can add up to ${maxRecipients.toLocaleString()} users at a time.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const { failedEmails = [] } = await onSubmit(submittableRecipients.map(({ email }) => email));
      const normalizedFailedEmails = new Set(failedEmails.map((email) => email.toLowerCase()));
      if (normalizedFailedEmails.size > 0) {
        setRecipients(recipients.filter((email) => normalizedFailedEmails.has(email)));
        setInput('');
      } else {
        onOpenChange(false);
      }
    } catch (error) {
      toast.error(typeof submitErrorMessage === 'function' ? submitErrorMessage(error) : submitErrorMessage);
      setInput('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !isSubmitting && onOpenChange(nextOpen)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3">{description}</div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="mt-5 rounded-lg bg-gray-100">
            <ButtonGroup className="w-full rounded-md ring-offset-2 has-[>input:focus-visible]:ring-1 has-[>input:focus-visible]:ring-teal-500">
              <Input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    stageInput();
                  }
                }}
                placeholder="name@example.com, colleague@example.com"
                aria-label="Email addresses"
                autoFocus
                disabled={isSubmitting}
                className="border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Button type="button" onClick={stageInput} disabled={isSubmitting} className="border-gray-300">
                {inputButtonLabel}
              </Button>
            </ButtonGroup>
            <p className="flex items-center gap-2 px-3.5 py-2.5 text-xs text-gray-500">
              <Icon className="lucide--info text-gray-400" /> Enter addresses separated by commas, spaces or new lines.
            </p>
          </div>

          <div className="mt-6">
            <div className="px-3 pb-2 text-sm font-medium text-gray-600">
              Users <span className="font-normal text-gray-400">({recipients.length})</span>
            </div>
            <hr />
            <div
              ref={recipientsScrollRef}
              className="recipients-scroll-fade max-h-64 min-h-28 divide-y divide-gray-200 overflow-y-auto"
            >
              {recipientPresentations.length === 0 ? (
                <div className="flex h-28 items-center justify-center px-3 py-1 text-center text-gray-400">
                  <p className="max-w-sm text-balance">Add email addresses above to review them before continuing.</p>
                </div>
              ) : (
                recipientPresentations.map(({ email, disabled, emailSuffix, badge }) => (
                  <div key={email} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <span className={disabled ? 'text-gray-400' : 'mr-2 text-gray-700'}>{email}</span>
                      {emailSuffix}
                      {badge}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecipients((current) => current.filter((recipient) => recipient !== email))}
                      disabled={isSubmitting}
                      aria-label={`Remove ${email}`}
                    >
                      <Icon className="lucide--x" />
                    </Button>
                  </div>
                ))
              )}
            </div>
            {submittableRecipients.length > maxRecipients && (
              <p role="alert" className="px-3 pt-2 text-sm text-red-700">
                Add up to {maxRecipients.toLocaleString()} users at a time.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleSubmit()}
            disabled={
              isSubmitting || submittableRecipients.length === 0 || submittableRecipients.length > maxRecipients
            }
          >
            {isSubmitting ? 'Adding…' : submitButtonLabel(submittableRecipients.length)}
            <Icon className={isSubmitting ? 'lucide--loader animate-spin ease-in-out' : 'lucide--chevron-right'} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
